import React, { useMemo } from 'react';
import qrcode from 'qrcode-generator';
import { tr } from '../i18n';

/**
 * QR code as inline SVG (spec § 7).
 *
 * SVG rather than a canvas or an image: it stays sharp when a supplier leans in
 * to scan it from a phone held at arm's length, and it needs no raster step.
 *
 * Type 0 = auto-size to the data; error level M, as the spec specifies. A
 * vCard pushes the version up quite high, which is fine — what matters is that
 * `qrcode-generator` picks a version that fits rather than throwing.
 */
export default function Qr({ value, size = 180, label }) {
  const path = useMemo(() => {
    if (!value) return null;
    try {
      const qr = qrcode(0, 'M');
      qr.addData(value);
      qr.make();
      const count = qr.getModuleCount();
      const d = [];
      for (let r = 0; r < count; r += 1) {
        for (let c = 0; c < count; c += 1) {
          if (qr.isDark(r, c)) d.push(`M${c} ${r}h1v1h-1z`);
        }
      }
      return { d: d.join(''), count };
    } catch (e) {
      // Too much data for any version, or an encoding the library refuses.
      return null;
    }
  }, [value]);

  if (!path) {
    return (
      <div className="ft-qr ft-qr-empty" style={{ width: size, height: size }}>
        <span>{tr("Nothing to encode yet")}</span>
      </div>
    );
  }

  return (
    <figure className="ft-qr" style={{ width: size }}>
      <svg
        viewBox={`0 0 ${path.count} ${path.count}`}
        width={size}
        height={size}
        role="img"
        aria-label={label || 'QR code'}
        shapeRendering="crispEdges"
      >
        {/* White quiet zone is part of the spec for scannability — never
            transparent, or a dark page background swallows the code. */}
        <rect width={path.count} height={path.count} fill="#ffffff" />
        <path d={path.d} fill="#0e1b33" />
      </svg>
      {label && <figcaption>{label}</figcaption>}
    </figure>
  );
}
